<!-- sections/DateWithinMonthsSection.vue -->
<script setup lang="ts">
import { nextTick, computed, watch, ref } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import ConnxField, { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import { numericMinMax } from '@/validation/numeric-min-max';
import { useDateRangeSettings } from '../composables/useDateRangeSettings';

interface Props {
  previousMonths?: {
    labelPrefix: string;
    labelAffix: string;
    value: number | null;
    min?: number;
    max?: number;
  };
  previousRange?: {
    selected: boolean;
    value: [string, string];
    type: string;
  };
  withinXMonths?: {
    labelPrefix: string;
    labelAffix: string;
    value: number | null;
    min?: number;
  };
  errors?: Map<string, string | null>;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
  showTitle: false,
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
  'update:within-x-months': [value: number | null];
}>();

// Use the SAME composable - it will be reactive to prop changes
const { withinXMonths, withinXMonthsMax } = useDateRangeSettings(props, emit);

const handleFieldError = (error: ErrorUpdate) => {
  emit('field-error', error);
};

const shouldShow = computed(() => {
  return props.withinXMonths !== undefined;
});

watch(withinXMonthsMax, async (newMax) => {
  if (
    props.withinXMonths !== undefined &&
    withinXMonths.value !== null &&
    newMax !== undefined &&
    withinXMonths.value > newMax
  ) {
    const previousWithinXMonths = withinXMonths.value;
    withinXMonths.value = null;
    await nextTick();
    withinXMonths.value = previousWithinXMonths;
  } else {
    handleFieldError({ name: 'withinXMonths', error: null });
  }
});
</script>

<template>
  <BaseSection title="Reporting Period" :should-show="shouldShow" :show-title="showTitle">
    <!-- Within X Months -->
    <div v-if="props.withinXMonths !== undefined" class="tw-my-4">
      <div class="tw-flex tw-items-start tw-gap-2">
        <label class="Field__label tw-mt-2" for="withinXMonths">
          {{ props.withinXMonths?.labelPrefix }}
        </label>
        <ConnxField
          name="withinXMonths"
          :key="`withinXMonthsMax-${withinXMonthsMax}`"
          v-model="withinXMonths"
          class="tw-w-52"
          :rules="numericMinMax(1, withinXMonthsMax)"
          required
          :type="InputType.Number"
          @update:error="handleFieldError"
        />
        <label class="Field__label tw-mt-2" for="withinXMonths">
          {{ props.withinXMonths?.labelAffix }}
        </label>
      </div>
    </div>
  </BaseSection>
</template>
